import { TableOptions } from '@/types/table';
import {
  ColumnSizingState,
  ExpandedState,
  GroupingState,
  PaginationState,
  RowPinningState,
  SortingState,
  VisibilityState,
} from '@tanstack/react-table';
import { Dispatch, RefObject, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useLazyLoading } from '../hooks/useLazyLoading';
import useTableStore from '../store/useTableStore';
import { TableProps } from '../Table';
import { useRowHighlight } from '../utils/useRowHighlight';
import { getNumberOfRowsByGrouping, getPageSize } from '../utils/utils';
import { useAutoResetPageIndex } from './useAutoResetPageIndex';
import { useExpandedSync, useTableInteractionSync } from './useTableSyncEffects';

export interface UseTableStoreSyncReturn<T> {
  expanded: ExpandedState;
  setExpanded: Dispatch<SetStateAction<ExpandedState>>;
  rowExpandedDelayed: Record<string, boolean>;
  setRowExpandedDelayed: Dispatch<SetStateAction<Record<string, boolean>>>;
  globalFilter: TableProps<T>['filter'];
  setGlobalFilter: Dispatch<SetStateAction<TableProps<T>['filter']>>;
  rowSelection: Record<number, boolean>;
  setRowSelection: Dispatch<SetStateAction<Record<number, boolean>>>;
  isAdjustTableOpen: boolean;
  setIsAdjustTableOpen: Dispatch<SetStateAction<boolean>>;
  tableWrapperRef: RefObject<HTMLDivElement | null>;
  columnIdKey: string;
  rowSelectionEnabled: boolean;
  pagination: PaginationState;
  setPagination: (id: string, x: Partial<PaginationState>) => void;
  sorting: SortingState;
  setSorting: (id: string, x: SortingState) => void;
  setLoading: (id: string, x: boolean) => void;
  setTotalRecords: (id: string, x: number) => void;
  grouping: GroupingState;
  setGrouping: (id: string, x: GroupingState) => void;
  rowPinning: RowPinningState;
  setRowPinning: (id: string, x: RowPinningState) => void;
  columnOrder: string[];
  columnSizing: ColumnSizingState;
  columnVisibility: VisibilityState;
  totalCount: number;
  lazyConfig: TableOptions<T>['lazy'];
  pageSize: number;
  autoResetPageIndex: boolean;
  setColumnOrder: (id: string, columnIdKey: string, columnOrder: string[]) => void;
  setColumnSizing: (id: string, columnIdKey: string, columnSizing: ColumnSizingState) => void;
  resetColumnState: (id: string, columnIdKey: string) => void;
}

export type UseTableStoreSyncParams<T> = Pick<
  TableProps<T>,
  'columns' | 'data' | 'filter' | 'options' | 'isLoading' | 'externalInteractionSignal'
> & { id: string };

export const useTableStoreSync = <T>({
  columns,
  data,
  filter,
  options,
  id,
  isLoading,
  externalInteractionSignal,
}: UseTableStoreSyncParams<T>): UseTableStoreSyncReturn<T> => {
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [rowExpandedDelayed, setRowExpandedDelayed] = useState<Record<string, boolean>>({});
  const [globalFilter, setGlobalFilter] = useState(filter);
  const [rowSelection, setRowSelection] = useState<Record<number, boolean>>({});
  const [isAdjustTableOpen, setIsAdjustTableOpen] = useState(false);
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const previousFilterRef = useRef(filter);

  const columnIdKey = useMemo(() => columns.map(col => String(col.id)).join(','), [columns]);

  const onRowSelection = options?.onRowSelection;
  const rowSelectionEnabled = !!options?.onRowSelection;
  const selectedElement = useMemo(() => {
    const [selectedKey] = Object.keys(rowSelection);
    return data[Number(selectedKey)];
  }, [data, rowSelection]);

  const {
    pagination,
    setPagination,
    sorting,
    setSorting,
    setLoading,
    setTotalRecords,
    selectedRow,
    grouping,
    setGrouping,
    rowPinning,
    setRowPinning,
    columnOrderMap,
    columnSizingMap,
    setColumnOrder,
    setColumnSizing,
    resetColumnState,
  } = useTableStore(
    useShallow(state => ({
      pagination: state.state[id].pagination,
      setPagination: state.setPagination,
      setSorting: state.setSorting,
      sorting: state.state[id].sorting,
      setLoading: state.setLoading,
      setTotalRecords: state.setTotalRecords,
      selectedRow: state.state[id].selectedRow,
      grouping: state.state[id].grouping,
      setGrouping: state.setGrouping,
      rowPinning: state.state[id].rowPinning,
      setRowPinning: state.setRowPinning,
      columnOrderMap: state.state[id].columnOrderMap,
      columnSizingMap: state.state[id].columnSizingMap,
      setColumnOrder: state.setColumnOrder,
      setColumnSizing: state.setColumnSizing,
      resetColumnState: state.resetColumnState,
    })),
  );

  const columnOrder = useMemo(() => columnOrderMap[columnIdKey] ?? [], [columnOrderMap, columnIdKey]);
  const columnSizing = useMemo(() => columnSizingMap[columnIdKey] ?? {}, [columnSizingMap, columnIdKey]);

  useRowHighlight({
    id,
    fullData: data,
    currentData: data,
    pagination,
    setPagination,
  });

  const lazyConfig = options?.lazy;
  useLazyLoading({ lazyConfig, pagination, sorting });

  useEffect(() => {
    setLoading(id, isLoading ?? false);
  }, [id, isLoading, setLoading]);

  const groupingKey = useMemo(() => JSON.stringify(grouping), [grouping]);

  useExpandedSync({
    defaultExpanded: options?.defaultExpanded,
    groupingKey,
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
    setExpanded,
  });

  const totalCount = lazyConfig?.totalCount ?? getNumberOfRowsByGrouping(data, grouping);

  useEffect(() => {
    setTotalRecords(id, totalCount);
  }, [id, setTotalRecords, totalCount]);

  useEffect(() => {
    // prevent page overflow on table with lazyConfig. (tables without lazyConfig don't need it it is handled by useAutoResetPageIndex logic)
    if (!lazyConfig || totalCount <= 0 || pagination.pageSize <= 0) return;
    const pageCount = Math.ceil(totalCount / pagination.pageSize);
    if (pagination.pageIndex >= pageCount) {
      setPagination(id, { pageIndex: 0 });
    }
  }, [id, lazyConfig, totalCount, pagination.pageIndex, pagination.pageSize, setPagination]);

  const columnVisibility = useMemo(
    () => Object.fromEntries(columns.map(col => [col.id, col.visible ?? true])),
    [columns],
  );

  useTableInteractionSync({
    data,
    filter,
    id,
    initialHorizontalScroll: options?.initialHorizontalScroll,
    onRowSelection,
    pageIndex: pagination.pageIndex,
    previousFilterRef,
    rowExtension: options?.rowExtension,
    rowSelection,
    rowSelectionEnabled,
    selectedElement,
    selectedRow,
    setGlobalFilter,
    setPagination,
    setRowExpandedDelayed,
    setRowSelection,
    tableWrapperRef,
  });

  const pageSize = useMemo(
    () =>
      getPageSize({
        options: {
          pageSize: pagination.pageSize ?? options?.pageSize,
        },
      }),
    [options?.pageSize, pagination.pageSize],
  );

  const sortingKey = useMemo(() => JSON.stringify(sorting), [sorting]);
  const autoResetPageIndex = useAutoResetPageIndex({
    filter,
    sortingKey,
    groupingKey,
    externalInteractionSignal,
    hasLazyLoading: !!lazyConfig,
  });

  useEffect(() => {
    setPagination(id, { pageSize });
    setTotalRecords(id, totalCount);
    setGrouping(id, options?.grouping || []);
    setRowPinning(id, { top: [], bottom: [] });
  }, [id, pageSize, options?.grouping, setGrouping, setPagination, setRowPinning, setTotalRecords, totalCount]);

  return {
    expanded,
    setExpanded,
    rowExpandedDelayed,
    setRowExpandedDelayed,
    globalFilter,
    setGlobalFilter,
    rowSelection,
    setRowSelection,
    isAdjustTableOpen,
    setIsAdjustTableOpen,
    tableWrapperRef,
    columnIdKey,
    rowSelectionEnabled,
    pagination,
    setPagination,
    sorting,
    setSorting,
    setLoading,
    setTotalRecords,
    grouping,
    setGrouping,
    rowPinning,
    setRowPinning,
    columnOrder,
    columnSizing,
    columnVisibility,
    totalCount,
    lazyConfig,
    pageSize,
    autoResetPageIndex,
    setColumnOrder,
    setColumnSizing,
    resetColumnState,
  };
};
